import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, Typography, FormControlLabel } from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { format, isValid, parse, parseISO } from 'date-fns';
import LoadingSwitch from '../../../../components/LoadingSwitch';
import { minScheduleDate, shiftEndDate } from '../../utils/dateUtils';

const toDate = (dateStr) => (dateStr ? parseISO(dateStr) : null);
const toTime = (timeStr) => (timeStr ? parse(timeStr, 'HH:mm', new Date()) : null);
const fieldSlotProps = { textField: { size: 'small', fullWidth: true, required: true } };

// MUI's minDate only greys out the calendar; a typed date still reaches onChange, hence the guards.
export const ScheduleStartField = ({ formState, handleFormChange, handleBatchSendChange }) => {
    const minDate = minScheduleDate(new Date());

    const handleChange = (date) => {
        if (!isValid(date) || date < minDate) return;

        const newDate = format(date, 'yyyy-MM-dd');
        const { isBatchSend, scheduledFor, scheduledEndDate } = formState;
        if (isBatchSend && scheduledFor && scheduledEndDate) {
            handleBatchSendChange('scheduledEndDate', shiftEndDate(scheduledFor, newDate, scheduledEndDate));
        }
        handleFormChange({ target: { name: 'scheduledFor', value: newDate } });
    };

    return (
        <DatePicker
            label={formState.isBatchSend ? 'Start Date' : 'Scheduled For'}
            value={toDate(formState.scheduledFor)}
            onChange={handleChange}
            minDate={minDate}
            format="yyyy-MM-dd"
            slotProps={fieldSlotProps}
        />
    );
};

export const BatchSendFields = ({ formState, handleBatchSendChange }) => {
    const [isWeekendPauseLoading, setIsWeekendPauseLoading] = useState(false);
    const startDate = toDate(formState.scheduledFor);

    const handleEndDateChange = (date) => {
        if (!isValid(date) || (startDate && date < startDate)) return;
        handleBatchSendChange('scheduledEndDate', format(date, 'yyyy-MM-dd'));
    };

    const timeChangeHandler = (name) => (time) => {
        if (isValid(time)) handleBatchSendChange(name, format(time, 'HH:mm'));
    };

    const handleWeekendPauseChange = async () => {
        setIsWeekendPauseLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 300));
            handleBatchSendChange('pauseWeekends', !formState.pauseWeekends);
        } finally {
            setIsWeekendPauseLoading(false);
        }
    };

    return (
        <>
            <Grid item xs={12}>
                <DatePicker
                    label="End Date"
                    value={toDate(formState.scheduledEndDate)}
                    onChange={handleEndDateChange}
                    minDate={startDate ?? undefined}
                    format="yyyy-MM-dd"
                    slotProps={fieldSlotProps}
                />
            </Grid>
            <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Between the hours
                </Typography>
            </Grid>
            <Grid item xs={6}>
                <TimePicker
                    label="Start Time"
                    value={toTime(formState.sendingTimeStart)}
                    onChange={timeChangeHandler('sendingTimeStart')}
                    ampm={false}
                    format="HH:mm"
                    slotProps={fieldSlotProps}
                />
            </Grid>
            <Grid item xs={6}>
                <TimePicker
                    label="End Time"
                    value={toTime(formState.sendingTimeEnd)}
                    onChange={timeChangeHandler('sendingTimeEnd')}
                    ampm={false}
                    format="HH:mm"
                    slotProps={fieldSlotProps}
                />
            </Grid>
            <Grid item xs={12}>
                <FormControlLabel
                    control={
                        <LoadingSwitch
                            checked={formState.pauseWeekends || false}
                            onChange={handleWeekendPauseChange}
                            loading={isWeekendPauseLoading}
                        />
                    }
                    label="Pause on Weekends"
                />
            </Grid>
        </>
    );
};

const schedulePropTypes = {
    formState: PropTypes.object.isRequired,
    handleBatchSendChange: PropTypes.func.isRequired
};

ScheduleStartField.propTypes = { ...schedulePropTypes, handleFormChange: PropTypes.func.isRequired };
BatchSendFields.propTypes = schedulePropTypes;
